import { useState, useEffect, useRef } from 'react';

import {
  SIDEBAR_COLLAPSED_WIDTH,
  POPUP_GAP,
  SIDEBAR_TRANSITION_MS,
} from '../ai-assistant.constants';

interface OnboardingPosition {
  top: number;
  left: number;
}

interface UseOnboardingPositionOptions {
  enabled?: boolean;
}

const useOnboardingPosition = (
  selector: string,
  isCollapsed: boolean,
  { enabled = true }: UseOnboardingPositionOptions = {},
): OnboardingPosition | null => {
  const [position, setPosition] = useState<OnboardingPosition | null>(null);
  const collapsedRef = useRef<boolean>(isCollapsed);

  collapsedRef.current = isCollapsed;

  useEffect(() => {
    if (!enabled) return undefined;

    let rafId: number | undefined;
    let running = true;

    const track = (): void => {
      if (!running) return;

      const el = document.querySelector(selector);

      if (el) {
        const rect = el.getBoundingClientRect();
        const top = rect.top;
        const left = collapsedRef.current
          ? SIDEBAR_COLLAPSED_WIDTH
          : rect.right + POPUP_GAP;

        setPosition((prev) => {
          if (prev?.top === top && prev?.left === left) return prev;

          return { top, left };
        });
      }

      rafId = requestAnimationFrame(track);
    };

    const timeoutId = setTimeout(() => {
      rafId = requestAnimationFrame(track);
    }, SIDEBAR_TRANSITION_MS);

    return () => {
      running = false;
      clearTimeout(timeoutId);
      if (rafId !== undefined) cancelAnimationFrame(rafId);
    };
  }, [selector, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const el = document.querySelector(selector);

    if (!el) return;

    const left = isCollapsed
      ? SIDEBAR_COLLAPSED_WIDTH
      : el.getBoundingClientRect().right + POPUP_GAP;

    setPosition((prev) => {
      if (!prev || prev.left === left) return prev;

      return { ...prev, left };
    });
  }, [isCollapsed, selector, enabled]);

  return enabled ? position : null;
};

export default useOnboardingPosition;
